import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import type { AnalysisDefinition } from '../api/types';
import { useSaveAnalysisVersionMutation } from '../api/useSaveAnalysisVersionMutation';
import { getAnalysisVersionPath } from '../routes';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import {
  selectCohortProfileId,
  selectServerSnapshot,
  selectSourceOrder,
  selectSources,
} from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseAnalysisSaveModalArgs, UseAnalysisSaveModalResult } from '../types';
import { applySourceCohort, getSavedSourceOrder } from '../utils/analysisSources';

export const useAnalysisSaveModal = ({
  onClose,
}: UseAnalysisSaveModalArgs): UseAnalysisSaveModalResult => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { projectId, analysisProfileId, analysisVersionId } = useAnalysisRouteParams();

  const cohortProfileId = useAnalysisDefinitionStore(selectCohortProfileId);
  const sources = useAnalysisDefinitionStore(selectSources);
  const sourceOrder = useAnalysisDefinitionStore(selectSourceOrder);
  const serverSnapshot = useAnalysisDefinitionStore(selectServerSnapshot);

  const { mutate, isPending, error, reset } = useSaveAnalysisVersionMutation();

  const runSave = useCallback(() => {
    if (!projectId || !analysisProfileId || !analysisVersionId || !serverSnapshot) return;
    // `tables` isn't editable from this screen; `sources` carries the staged
    // variable additions on top of the pristine snapshot.
    const stagedDefinition: AnalysisDefinition = {
      sources: sources ?? serverSnapshot.definition?.sources ?? {},
      tables: serverSnapshot.definition?.tables ?? [],
    };
    const definitionWithCohort = applySourceCohort(stagedDefinition, cohortProfileId);
    mutate(
      {
        projectId,
        analysisProfileId,
        analysisVersionId,
        definition: {
          ...definitionWithCohort,
          // Computed after the cohort edit so the order lists the final set of sources.
          sourceOrder: getSavedSourceOrder({ sources: definitionWithCohort.sources, sourceOrder }),
        },
      },
      {
        onSuccess: (envelope) => {
          // `replace: true` so the back button doesn't return to the pre-save
          // URL (which holds the now-superseded version id).
          navigate(
            getAnalysisVersionPath(projectId, analysisProfileId, envelope.data.analysisVersionId),
            { replace: true }
          );
          onClose();
        },
      }
    );
  }, [
    projectId,
    analysisProfileId,
    analysisVersionId,
    serverSnapshot,
    sources,
    sourceOrder,
    cohortProfileId,
    mutate,
    navigate,
    onClose,
  ]);

  // Auto-fire on mount — the modal exists to block the UI while the save
  // round-trip runs. The ref guard keeps the dispatch idempotent under React
  // StrictMode's dev double-invoke of mount effects; Retry re-fires explicitly.
  const hasFiredRef = useRef(false);
  useEffect(
    () => {
      if (hasFiredRef.current) return;
      hasFiredRef.current = true;
      runSave();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onRetry = useCallback(() => {
    reset();
    runSave();
  }, [reset, runSave]);

  return {
    title: formatMessage({ id: 'analysisDetail.saveModal.title' }),
    loadingTitle: formatMessage({ id: 'analysisDetail.saveModal.loadingTitle' }),
    loadingText: formatMessage({ id: 'analysisDetail.saveModal.loadingText' }),
    errorTitle: formatMessage({ id: 'analysisDetail.saveModal.errorTitle' }),
    errorText: formatMessage({ id: 'analysisDetail.saveModal.errorText' }),
    retryButtonLabel: formatMessage({ id: 'analysisDetail.saveModal.retryButton' }),
    isSubmitting: isPending,
    submittingError: error ?? null,
    onRetry,
  };
};
